import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import {
  Copy,
  FileText,
  Loader2,
  MoreHorizontal,
  Pin,
  PinOff,
  Plus,
  Trash2,
} from 'lucide-react'

import { useArtifactCanvas } from '@/components/artifact/artifact-canvas-context'
import { getPageHeader } from '@/constants'
import { Button } from '@/components/ui/button'
import { toast } from 'sonner'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'
import { PageHeaderLeading } from '@/components/navigation/page-header-leading'
import { PageHeaderStack } from '@/components/navigation/page-header-stack'
import { PageHeaderActionTertiary } from '@/components/navigation/page-header-actions'
import { InitialsAvatar } from '@/components/ui/initials-avatar'
import { Skeleton } from '@/components/ui/skeleton'
import {
  useCreateDocument,
  useDeleteDocument,
  useDocuments,
  useDuplicateDocument,
  usePinDocument,
  useUnpinDocument,
  type DocumentListItem,
} from '@/hooks/use-documents'
import { DocumentsEmptyState } from './documents-empty-state'
import { displayDocumentTitle, DOCUMENT_QUERY_PARAM } from './documents-shared'
import { openDocumentCanvas } from './open-document-canvas'
import { cn } from '@/lib/utils'

function formatDocumentDate(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleDateString(undefined, { dateStyle: 'medium' })
}

function isDocumentsIndexPath(pathname: string): boolean {
  return pathname === '/documents' || pathname === '/documents/'
}

function DocumentRow({
  document,
  isOpen,
  onOpen,
  onDelete,
  onDuplicate,
  onTogglePin,
  isPinPending,
  isDuplicatePending,
}: {
  document: DocumentListItem
  isOpen: boolean
  onOpen: () => void
  onDelete: () => void
  onDuplicate: () => void
  onTogglePin: () => void
  isPinPending: boolean
  isDuplicatePending: boolean
}) {
  return (
    <div
      className={cn(
        'group flex items-center gap-3 py-3',
        'transition-colors hover:bg-muted/50',
        isOpen && 'bg-muted/50',
      )}
    >
      <button
        type="button"
        onClick={onOpen}
        className="flex min-w-0 flex-1 items-center gap-3 text-left"
      >
        <FileText className="size-4 shrink-0 text-muted-foreground" />
        <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
          {displayDocumentTitle(document.title)}
        </span>
        <span className="flex shrink-0 items-center gap-2 text-xs text-muted-foreground">
          {document.created_by_name ? (
            <span className="flex min-w-0 max-w-[9rem] items-center gap-1.5">
              <InitialsAvatar
                src={document.created_by_avatar_url}
                colorSeed={document.created_by_id ?? document.created_by_name}
                className="size-6 shrink-0"
                alt={document.created_by_name}
              />
              <span className="truncate">{document.created_by_name}</span>
            </span>
          ) : null}
          {document.created_by_name ? (
            <span className="text-border" aria-hidden>
              ·
            </span>
          ) : null}
          <span className="shrink-0 tabular-nums">{formatDocumentDate(document.updated_at)}</span>
        </span>
      </button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <PageHeaderActionTertiary
            aria-label="Document actions"
            className="shrink-0 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100"
            pressFeedback
          >
            <MoreHorizontal className="size-4" />
          </PageHeaderActionTertiary>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={onTogglePin} disabled={isPinPending}>
            {document.is_pinned ? (
              <>
                <PinOff data-icon="inline-start" />
                Unpin from sidebar
              </>
            ) : (
              <>
                <Pin data-icon="inline-start" />
                Pin to sidebar
              </>
            )}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onDuplicate} disabled={isDuplicatePending}>
            <Copy data-icon="inline-start" />
            Duplicate
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={onDelete}>
            <Trash2 data-icon="inline-start" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}

export function DocumentsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const location = useLocation()
  const documentId = searchParams.get(DOCUMENT_QUERY_PARAM)
  const { openCanvas, closeCanvas, isOpen: canvasOpen } = useArtifactCanvas()
  const prevCanvasOpen = useRef(false)

  const { data: documents = [], isLoading, error } = useDocuments()
  const createDocument = useCreateDocument()
  const deleteDocument = useDeleteDocument()
  const duplicateDocument = useDuplicateDocument()
  const pinDocument = usePinDocument()
  const unpinDocument = useUnpinDocument()
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [pinBusyId, setPinBusyId] = useState<string | null>(null)
  const [duplicateBusyId, setDuplicateBusyId] = useState<string | null>(null)
  const [createError, setCreateError] = useState<string | null>(null)

  const selectedDocument = documents.find((doc) => doc.id === documentId)

  const header = getPageHeader(
    '/documents',
    'Documents',
    'Shared pages for your team — simple markdown notes.',
  )

  const setDocumentParam = useCallback(
    (id: string) => {
      if (searchParams.get(DOCUMENT_QUERY_PARAM) === id) return
      const next = new URLSearchParams(searchParams)
      next.set(DOCUMENT_QUERY_PARAM, id)
      setSearchParams(next, { replace: false })
    },
    [searchParams, setSearchParams],
  )

  const clearDocumentParam = useCallback(() => {
    const next = new URLSearchParams(searchParams)
    next.delete(DOCUMENT_QUERY_PARAM)
    setSearchParams(next, { replace: true })
  }, [searchParams, setSearchParams])

  useEffect(() => {
    if (!documentId) {
      closeCanvas()
      return
    }

    openDocumentCanvas({
      documentId,
      titleHint: selectedDocument?.title ?? '',
      openCanvas,
      closeCanvas,
      onDeleted: clearDocumentParam,
      onDuplicated: setDocumentParam,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- open when deep-link id changes only
  }, [documentId])

  useEffect(() => {
    if (prevCanvasOpen.current && !canvasOpen && documentId) {
      if (isDocumentsIndexPath(location.pathname)) {
        clearDocumentParam()
      }
    }
    prevCanvasOpen.current = canvasOpen
  }, [canvasOpen, clearDocumentParam, documentId, location.pathname])

  const handleCreate = async () => {
    setCreateError(null)
    try {
      const created = await createDocument.mutateAsync()
      setDocumentParam(created.id)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not create document'
      setCreateError(message)
      toast.error(message)
    }
  }

  const handleTogglePin = async (doc: DocumentListItem) => {
    setPinBusyId(doc.id)
    try {
      if (doc.is_pinned) {
        await unpinDocument.mutateAsync(doc.id)
      } else {
        await pinDocument.mutateAsync(doc.id)
      }
    } finally {
      setPinBusyId(null)
    }
  }

  const handleDuplicate = async (doc: DocumentListItem) => {
    setDuplicateBusyId(doc.id)
    try {
      const copy = await duplicateDocument.mutateAsync(doc.id)
      setDocumentParam(copy.id)
    } finally {
      setDuplicateBusyId(null)
    }
  }

  return (
    <PageContainer>
      <PageHeaderStack>
        <PageHeader
          leading={<PageHeaderLeading />}
          title={header.title}
          description={header.description}
          actions={
            <Button onClick={() => void handleCreate()} disabled={createDocument.isPending}>
              {createDocument.isPending ? (
                <Loader2 className="size-4 animate-spin" data-icon="inline-start" />
              ) : (
                <Plus data-icon="inline-start" />
              )}
              New document
            </Button>
          }
        />
      </PageHeaderStack>

      {createError ? (
        <p className="mb-4 text-sm text-destructive" role="alert">
          {createError}
        </p>
      ) : null}

      {isLoading ? (
        <div className="flex flex-col gap-1">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className="h-12 w-full" />
          ))}
        </div>
      ) : error ? (
        <p className="text-sm text-destructive">Could not load documents.</p>
      ) : documents.length === 0 ? (
        <DocumentsEmptyState
          onCreate={() => void handleCreate()}
          isCreating={createDocument.isPending}
        />
      ) : (
        <div className="flex flex-col">
          {documents.map((document) => (
            <DocumentRow
              key={document.id}
              document={document}
              isOpen={documentId === document.id}
              onOpen={() => setDocumentParam(document.id)}
              onDelete={() => setDeleteId(document.id)}
              onDuplicate={() => void handleDuplicate(document)}
              onTogglePin={() => void handleTogglePin(document)}
              isPinPending={pinBusyId === document.id}
              isDuplicatePending={duplicateBusyId === document.id}
            />
          ))}
        </div>
      )}

      <AlertDialog open={deleteId != null} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this document?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the page for everyone on Yes Human. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (!deleteId) return
                void deleteDocument.mutateAsync(deleteId).then(() => {
                  if (documentId === deleteId) {
                    closeCanvas()
                    clearDocumentParam()
                  }
                  setDeleteId(null)
                })
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  )
}
